import React, { useState, useRef, useContext } from "react";
import UploadedFileIcon from "../../../../assets/icons/uploaded-file-icon.svg";
import DeleteFileIcon from "../../../../assets/icons/delete-file.icon.svg";
import { ReactComponent as UploadIcon } from "../../../../assets/icons/upload-icon.svg";
import {
  DocumentUploadBox,
  Flex,
  Space,
  UploadButton,
  UploadWrapper,
} from "./styles";
import { NetworkErrorAlertContext } from "../../../../context/NetworkErrorAlert";

type Props = {
  onFileChange: (
    file: File | null,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  error?: string;
  showExtra?: boolean;
  accept?: string;
  maxSize?: number;
  mainText?: string;
  subtitle?: string;
  loading?: boolean;
  uploadedFileName?: string;
};

const DocumentUpload: React.FC<Props> = (props) => {
  const {
    onFileChange,
    error,
    showExtra = false,
    accept = "image/jpeg,image/png,application/pdf,.pdf",
    maxSize = 5 * 1024 * 1024, 
    mainText = "Upload file",
    subtitle = "PDF, JPEG or PNG less than 5MB",
    loading = false,
    uploadedFileName,
  } = props;

  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { onShowAlert } = useContext(NetworkErrorAlertContext);

  const isControlled = "uploadedFileName" in props;
  const displayName = isControlled ? uploadedFileName : file?.name;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      if (selectedFile.size > maxSize) {
        const maxSizeInMB = (maxSize / (1024 * 1024));
        onShowAlert("", `File size must be less than ${maxSizeInMB}MB.`);
        return;
      }
      setFile(selectedFile);
      onFileChange(selectedFile, event);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteFile = () => {
    setFile(null);
    onFileChange(null);
  };

  return (
    <Space>
      {!displayName || loading ? (
        <DocumentUploadBox>
          <input
            type="file"
            onChange={handleFileChange}
            style={{ display: "none" }}
            ref={fileInputRef}
            accept = {accept}
          />
          <UploadButton type="button" onClick={handleUploadClick}>
            <UploadWrapper>
              <UploadIcon />
              {showExtra && (
                <div>
                  <h1>{loading ? "Uploading..." : mainText}</h1>
                  <p>{loading ? "Please wait..." : subtitle}</p>
                </div>
              )}
            </UploadWrapper>
          </UploadButton>
          {error && <p>{error}</p>}
        </DocumentUploadBox>
      ) : (
        <DocumentUploadBox>
            <Flex>
              <div className="uploaded-content">
                <img src={UploadedFileIcon} alt="Uploaded file icon" />
                <span>
                  <p>{displayName}</p>
                  <p className="uploaded-copy">Uploaded</p>
                </span>
              </div>
              <button type="button" onClick={handleDeleteFile}>
                <img src={DeleteFileIcon} alt="Delete file icon" />
              </button>
            </Flex>
        </DocumentUploadBox>
      )}
    </Space>
  );
};

export default DocumentUpload;
